import type { ContractDispute } from '@/features/contracts/constants/dispute-labels';

/**
 * Types partagés pour les contrats
 * Utilisés par admin et outil-pro (liste + détail)
 */

/**
 * Statut calculé de la signature (source de vérité unique côté backend).
 * Priorité : handwritten > signed > canceled > pending. `failed` si pas de signature
 * mais contrat finalisé. `null` si aucune signature attendue.
 */
export type SignatureStatus = 'handwritten' | 'signed' | 'canceled' | 'pending' | 'failed' | null;

/**
 * Interface pour les informations de signature d'un contrat (vue liste)
 */
export interface ContractSignature {
  isHandwritten: boolean;
  isSent?: boolean;
  isIntegrated?: boolean;
  isCanceled?: boolean;
  recipientEmail?: string;
  recipientFirstname?: string;
  recipientLastname?: string;
  recipientPhone?: string;
}

/**
 * Interface pour une personne (souscripteur ou bénéficiaire)
 */
export interface Person {
  id?: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  phoneNumber2?: string;
  address?: string;
  address2?: string;
  cp?: string;
  city?: string;
  dateOfBirth?: string;
  civilityId?: number;
  society?: string;
}

/**
 * Interface simplifiée d'un contrat pour la vue liste
 */
export interface ContractListItem {
  id: number;
  label: string;
  externalId?: string;
  beneficiary?: {
    firstName: string;
    lastName: string;
    email?: string;
  };
  subscriber?: {
    firstName: string;
    lastName: string;
    email?: string;
    society?: string;
  };
  warranty?: {
    name: string;
    isCashPayment?: boolean;
    isMensualisable?: boolean;
  };
  company?: {
    uuid: string;
    name: string;
    monthlyPayment?: number;
    onlinePayment?: number;
  };
  creator?: {
    firstName: string;
    lastName: string;
  };
  signature?: ContractSignature;
  signatureStatus?: SignatureStatus;
  vehicle?: {
    registration?: string;
    brand?: string;
    model?: string;
  };
  dateSouscription: Date | string;
  dateCreation: Date | string;
  dateEnd: Date | string;
  sellingPrice: number;
  duration: number;
  monthlyPaymentId?: string;
  monthlyPaymentStatus?: string;
  monthlyPaymentReminder?: number;
  monthlyPaymentReminderDate?: string;
  monthlyPriceClient?: number;
  idStatus: number;
  cancelationRequested?: boolean;
  cancelationRequestedAt?: string;
  /** Liste des contrats : état du dernier litige Stripe, null sans litige. */
  latestDisputeStatus?: string | null;
  cancelationReason?: string;
  cancelationDetails?: string;
  existingDpNumber?: string;
  userId?: string;
  isUpsell?: boolean;
  /** Formule d'assistance figée à la souscription (`null` = aucune). */
  assistanceFormulaId?: number | null;
  /** Libellé de la formule, snapshot du contrat — jamais relu du référentiel. */
  assistanceLabel?: string | null;
}

/**
 * Filtres pour la liste des contrats
 */
export interface ContractFilters {
  skip?: number;
  take?: number;
  search?: string;
  duration?: number;
  paymentType?: string;
  creationDateFrom?: string;
  creationDateTo?: string;
  startDateFrom?: string;
  startDateTo?: string;
  status?: number | string;
  excludeStatus?: number;
  societyId?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
  companyId?: string;
  excludeWithDeliveryProof?: boolean;
  /**
   * Filtres services, indépendants l'un de l'autre : `undefined` = tous les
   * contrats (pas de filtre), `true` = uniquement avec, `false` = explicitement
   * sans. Le `false` doit rester distinct de l'absence de filtre.
   */
  hasUpsell?: boolean;
  hasAssistance?: boolean;
}

/**
 * Réponse paginée de la liste des contrats
 */
export interface ContractListResponse {
  contracts: ContractListItem[];
  total: number;
}

/**
 * États possibles d'un contrat
 */
export enum ContractStatus {
  ACTIVE = 1,
  EXPIRED = 2,
  CANCELLED = 3,
  SUSPENDED = 4,
}

/**
 * Labels des états
 */
export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  [ContractStatus.ACTIVE]: 'Actif',
  [ContractStatus.EXPIRED]: 'Expiré',
  [ContractStatus.CANCELLED]: 'Annulé',
  [ContractStatus.SUSPENDED]: 'Suspendu',
};

/**
 * Couleurs des badges par statut
 */
export const CONTRACT_STATUS_COLORS: Record<ContractStatus, string> = {
  [ContractStatus.ACTIVE]: 'success',
  [ContractStatus.EXPIRED]: 'secondary',
  [ContractStatus.CANCELLED]: 'danger',
  [ContractStatus.SUSPENDED]: 'warning',
};

/**
 * Statuts unifiés contrat + mensualisation
 */
export type UnifiedContractStatus =
  | 'pending_cancellation'
  | 'payment_initialized'
  | 'payment_ongoing'
  | 'active'
  | 'payment_failed'
  | 'cancelled'
  | 'expired';

export const UNIFIED_STATUS_LABELS: Record<UnifiedContractStatus, string> = {
  pending_cancellation: "En attente d'annulation",
  payment_initialized: 'Paiement initialisé',
  payment_ongoing: 'En cours de paiement',
  active: 'Actif',
  payment_failed: 'Paiement échoué',
  cancelled: 'Annulé',
  expired: 'Expiré',
};

export const UNIFIED_STATUS_STYLES: Record<UnifiedContractStatus, string> = {
  pending_cancellation: 'tw-bg-orange-100 tw-text-orange-800',
  payment_initialized: 'tw-bg-gray-100 tw-text-gray-800',
  payment_ongoing: 'tw-bg-blue-100 tw-text-blue-800',
  active: 'tw-bg-green-100 tw-text-green-800',
  payment_failed: 'tw-bg-red-100 tw-text-red-800',
  cancelled: 'tw-bg-gray-100 tw-text-gray-800',
  expired: 'tw-bg-gray-100 tw-text-gray-800',
};

export const UNIFIED_STATUS_VARIANTS: Record<UnifiedContractStatus, string> = {
  pending_cancellation: 'pending_signature',
  payment_initialized: 'default',
  payment_ongoing: 'info',
  active: 'active',
  payment_failed: 'error',
  cancelled: 'cancelled',
  expired: 'expired',
};

/**
 * Détermine le statut unifié d'un contrat en tenant compte de la mensualisation
 */
export function getUnifiedContractStatus(contract: { idStatus: number; monthlyPaymentId?: string | null; monthlyPaymentStatus?: string | null; cancelationRequested?: boolean }): UnifiedContractStatus {
  // En attente d'annulation prime (sauf si déjà annulé)
  if (contract.cancelationRequested && contract.idStatus !== ContractStatus.CANCELLED) return 'pending_cancellation';

  // Expiré prime sur tout
  if (contract.idStatus === ContractStatus.EXPIRED) return 'expired';

  // Un contrat annulé (idStatus=3) reste annulé quel que soit l'état de la mensualisation.
  // Sans ce check avant la branche monthlyPaymentId, un contrat avec mps='succeeded' historique
  // ressortirait comme 'active' alors que idStatus=3.
  if (contract.idStatus === ContractStatus.CANCELLED) return 'cancelled';

  // Si mensualisation active, utiliser le statut de paiement
  if (contract.monthlyPaymentId) {
    const mps = contract.monthlyPaymentStatus;
    if (mps === 'initialized') return 'payment_initialized';
    if (mps === 'ongoing' || mps === 'retry_in_progress') return 'payment_ongoing';
    if (mps === 'succeeded' || mps === 'completed' || mps === 'payment_failed_twice' || mps === 'payment_method_updated') return 'active';
    if (mps === 'failed') return 'payment_failed';
    if (mps === 'canceled_undone' || mps === 'canceled_unpaid' || mps === 'canceled_manual' || mps === 'canceled_dispute_lost') return 'cancelled';
  }

  // Sans mensualisation, utiliser le statut contrat classique
  if (contract.idStatus === ContractStatus.ACTIVE) return 'active';
  if (contract.idStatus === ContractStatus.SUSPENDED) return 'payment_ongoing';

  return 'active';
}

/**
 * Brouillon de contrat
 */
export interface ContractDraft {
  id: number;
  uuid: string;
  societyId: string;
  userId: string;
  currentStep: number;
  formData: Record<string, any>;
  label: string | null;
  createdAt: string;
  updatedAt: string;
  /** Renseigné uniquement pour les utilisateurs INTERNAL (admin) */
  societyName?: string | null;
  /** Renseigné uniquement pour les utilisateurs INTERNAL (admin) */
  creatorName?: string | null;
}

// ==================== Types pour la vue détail ====================

/**
 * Interface pour les informations de créateur d'un contrat
 */
export interface ContractCreator {
  firstName: string;
  lastName: string;
  email?: string;
  type: 'EXTERNAL' | 'INTERNAL';
}

/**
 * Interface pour les informations véhicule
 * Mapping des clés CHAMP_FORMULAIRE
 */
export interface VehicleData {
  registration?: string;      // ID 1
  serialNumber?: string;      // ID 2
  entryIntoService?: string;  // ID 3
  brand?: string;             // ID 4
  model?: string;             // ID 5
  finishes?: string;          // ID 6
  fuel?: string;              // ID 7
  fiscalPower?: string;       // ID 8
  displacement?: string;      // ID 9
  enginePower?: string;       // ID 10
  kmStart?: string;           // ID 11
  price?: string;             // ID 12
}

export interface ContractDocument {
  id: number;
  name: string;
  fileName: string;
  mimeType?: string;
}

/**
 * Interface complète d'un contrat
 * Mapping de METIER_CONTRAT_CLIENT
 */
export interface Contract {
  id: number;
  label: string;
  externalId: string;
  subscriptionUuid: string;
  idWarranty: number;
  idPartner: number;
  idBeneficiary: number;
  idSubscriber: number;
  idDocument: number;
  idStatus: number;
  type: number;
  duration: number;
  sellingPrice: number;
  userId: string;
  warrantyFinancialCap: number;
  warrantyKmLimit: number;
  dateCreation: string;
  dateSouscription: string;
  dateUpdate: string;
  dateEnd: string;
  dateCanceled?: string;
  documentIds: number[];
  monthlyPaymentId?: string;
  monthlyPaymentStatus?: string;
  monthlyPaymentReminder?: number;
  monthlyPaymentReminderDate?: string;
  monthlyPriceClient?: number;

  // Champs financiers
  quantity: number;
  authorizedClaims: number;
  amountTTC: number;
  amountHT: number;
  unitPrice: number;

  affinicarCommissionRate: number;
  affinicarCommissionAmount: number;
  partnerCommissionRate: number;
  partnerCommissionAmount: number;
  companyCommissionRate: number;
  companyCommissionAmount: number;

  insuranceTaxRate: number;
  insuranceTaxAmount: number;

  partnerPremium: number;
  affinicarPremium: number;
  companyPremium: number;

  minSellingPrice: number;
  maxSellingPrice: number;

  servicePrice: number;
  warrantyTransferPrice: number;
  warrantyMemberTransferPrice: number;
  /** Option rétrocession de la société vendeuse (config V2 effective) — rétro/factu à 0 si false */
  isTransferPrice?: boolean;
  warrantyProvision: number;

  templateName: string;
  includedOption: boolean;

  // Relations optionnelles
  beneficiary?: Person;
  subscriber?: Person;
  warranty?: {
    uuid: string;
    name: string;
    duration: number;
    warrantyType?: number;
    societyId?: string;
  };
  company?: {
    uuid: string;
    name: string;
    code: string;
    monthlyPayment?: number;
  };
  vehicle?: VehicleData;
  society?: {
    uuid: string;
    name: string;
    address?: string;
    cp?: string;
    city?: string;
  };
  creator?: ContractCreator;
  signature?: ContractSignature;

  // Champs supplémentaires pour la vue détail
  kmStart?: number;
  vehicleSellingPrice?: number;
  paymentType?: number;
  signatureType?: number;
  signatureStatus?: SignatureStatus;
  monthlyPrice?: number;

  // Upsell
  isUpsell?: boolean;
  originalContractId?: number;
  upsellLeadId?: string;

  // Assistance — snapshot figé à la souscription, jamais relu du référentiel :
  // la garantie peut avoir changé de formule, le contrat signé ne change pas.
  assistanceFormulaId?: number | null;
  /** Libellé COURT, réutilisé tel quel sur la ligne de facture. */
  assistanceLabel?: string | null;
  /**
   * Prestations de la formule, figées à la souscription. Porte aussi `tier` et
   * `loanerDays`, qui servent à composer la phrase affichée au client — voir
   * `assistanceSentence`.
   */
  assistanceBenefits?: Record<string, unknown> | null;
  /** Supplément HT. S'ADDITIONNE à `servicePrice`, ne le remplace pas. */
  assistancePrice?: number | null;
  linkedContract?: {
    id: number;
    label: string;
    idStatus: number;
    relation: 'original' | 'upsell';
  };

  // Attestation de livraison existante
  existingDpNumber?: string;

  // Attestation de livraison
  deliveryProof?: {
    id: number;
    dpNumber?: string;
    state: string;
    createdAt: string;
  };

  // SAV
  savs?: any[];

  // Documents supplémentaires
  contractDocuments?: ContractDocument[];

  // Demande d'annulation
  cancelationRequested?: boolean;
  cancelationRequestedAt?: string;
  cancelationReason?: string | null;
  cancelationDetails?: string | null;
}

// ==================== Types pour la création de contrat ====================

/**
 * Interface pour une valeur de formulaire dynamique
 */
export interface ContractFormValue {
  key: string;
  value: any;
}

/**
 * DTO pour créer un contrat
 */
export interface CreateContractDto {
  societyId?: string;
  warrantyUuid: string;
  externalId?: string;
  subscriptionUuid?: string;
  subscriptionDate: string;
  extensionEndDate?: string;
  buyingDate?: string;
  subscriber: Person;
  beneficiary: Person;
  vehicle?: VehicleData;
  formValues?: ContractFormValue[];

  kmStart?: number;
  vehicleSellingPrice?: number;

  paymentType: 1 | 2;
  isOnlinePayment: boolean;
  paymentMethod?: 1 | 2 | 3;
  sellingPrice: number;
  monthlyPrice?: number;
  quantity?: number;
  includedOption?: boolean;
  includedOptionLabel?: string;
  /** @deprecated Ignoré par l'API (rétro/factu calculées côté serveur selon la config isTransferPrice) */
  headTransferPrice?: number;
  /** @deprecated Ignoré par l'API (rétro/factu calculées côté serveur selon la config isTransferPrice) */
  memberTransferPrice?: number;
  commission?: number;
  commissionType?: 1 | 2;

  signatureType?: 1 | 2;
  vehicleCheckReportUrl?: string;
  isIntermediary?: boolean;
  draftUuid?: string;
  type: number;
  userId?: string;
  documentIds?: number[];
  skipNotifications?: boolean;
  paymentExpirationHours?: number;
  /**
   * RGPD : le client refuse que ses données soient utilisées à des fins de
   * prospection. Renseigné uniquement si le vendeur a l'upsell activé.
   * Défaut false. Exclut le contrat des leads upsell côté backend.
   */
  prospectionOptOut?: boolean;
}

/**
 * DTO de réponse après création
 */
export interface ContractResponse {
  id: number;
  label: string;
  externalId: string;
  subscriptionUuid: string;
  dateSouscription: string;
  dateEnd: string;
  idStatus: number;
  sellingPrice: number;
  duration: number;
  documentId: number;
  paymentLink?: string;
  monthlyPaymentId?: string;
  signatureId?: string;
  signatureLink?: string;
  signatureLinkExpiration?: string;
  /** Absents quand l'envoi part après la réponse (`notificationsScheduled`) */
  emailSent?: boolean;
  smsSent?: boolean;
  /** E-mail et SMS envoyés juste après la réponse */
  notificationsScheduled?: boolean;
  message: string;
}

/**
 * Détails de paiement d'un contrat (mensualisation ou comptant)
 */
export interface PaymentDetails {
  paymentType: number;
  status: string;
  totalCount: number;
  paidCount: number;
  totalAmountTTC: number;
  paidAmount: number;
  remainingAmount: number;
  receivedAmount: number;
  remainingToReceive: number;
  totalFees: number;
  standardFees?: number;
  upsellFees?: number;
  isUpsell?: boolean;
  monthlyPrice: number;
  progressPercent: number;
  hasInvoice: boolean;
  invoiceType: 'echeancier' | 'stripe_installments' | 'invoice';
  /**
   * Mode de facturation mensuelle figé sur CE contrat (snapshot, source = garant).
   * true = factures Stripe individuelles, false = échéancier Affinicar.
   */
  useStripeInvoicesForMonthly: boolean;
  invoiceUrl: string | null;
  stripeInvoices: Array<{
    url: string;
    number: string;
    amount: number;
    date: string;
  }> | null;
  /**
   * Renseigné UNIQUEMENT quand le contrat est en mode « factures Stripe » et que
   * celles-ci n'ont pas pu être récupérées. Sert à afficher un état explicite
   * plutôt que de retomber silencieusement sur le bouton échéancier.
   *
   * `SUBSCRIPTION_NOT_YET_CREATED` n'est PAS une erreur : mandat SEPA non encore confirmé.
   */
  stripeInvoicesError:
    | 'NO_STRIPE_ACCOUNT'
    | 'NOT_A_SUBSCRIPTION'
    | 'SUBSCRIPTION_NOT_YET_CREATED'
    | 'NO_PAID_INVOICE'
    | 'CASH_INVOICE_UNAVAILABLE'
    | 'STRIPE_ERROR'
    | null;
  /**
   * Un échéancier `FAC_GRTMP` a réellement été émis et est rattaché au contrat.
   * Distinct de `hasInvoice` (qui vaut aussi true sur le seul statut `succeeded`).
   * Permet de proposer la pièce historique en mode Stripe sans déclencher la garde
   * de génération côté API (409).
   */
  hasHistoricalEcheancier: boolean;
  beneficiaryEmail: string | null;
  beneficiaryPhone: string | null;
  /** Litiges Stripe du contrat, du plus récent au plus ancien. */
  disputes?: ContractDispute[];
}

// ============================================
// Remboursement client à l'annulation
// ============================================

/** Dernier paiement remboursable proposé dans la modale d'annulation. */
export interface RefundablePaymentInfo {
  payment: {
    amountCents: number;
    /** Commission Affinicar prélevée sur ce paiement. */
    commissionCents: number;
    paymentDate: string;
    /** `processing` : prélèvement SEPA encore en cours de traitement. */
    status: 'succeeded' | 'processing';
  } | null;
  /** Nombre de paiements remboursables : seul le dernier est remboursé. */
  paymentsCount: number;
}

export type ContractRefundStatus =
  | 'waiting_payment'
  | 'waiting_funds'
  | 'creating'
  | 'pending'
  | 'succeeded'
  | 'failed'
  | 'canceled';

export type ContractRefundCommissionStatus =
  | 'not_requested'
  | 'waiting'
  | 'refunded'
  | 'manual_required'
  | 'failed';

/** Résultat de la demande de remboursement renvoyé par l'annulation. */
export interface ContractRefundRequestResult {
  refundId: number;
  status: ContractRefundStatus;
  amountCents: number;
  commissionCents: number;
  refundCommission: boolean;
  paymentsCount: number;
  failureReason: string | null;
}

/** Demande de remboursement affichée sur la fiche contrat. */
export interface ContractRefund {
  id: number;
  status: ContractRefundStatus;
  amountCents: number;
  paymentDate: string;
  paymentsCount: number;
  failureReason: string | null;
  refundCommission: boolean;
  commissionCents: number | null;
  commissionStatus: ContractRefundCommissionStatus;
  requestedAt: string;
  statusUpdatedAt: string;
  /** Annulation du contrat, ou report du 1er prélèvement (contrat qui continue). */
  reason: 'cancellation' | 'first_charge_postponed';
}
